import React, { useEffect } from 'react';
import { css } from '@emotion/core';

import { Box, Button } from '../../../design-system';
import { PickupStation as PickupStationType } from '../types';
import { Position } from '../../store-locator/types';
import { SEE_ON_THE_MAP, SELECT_PICKUP_STATION, SELECTED_PICKUP_STATION } from '../locale';
import StationInfo from './StationInfo';
import { useMediaQueries } from '../../common/hooks/useMediaQuery';

type Props = {
  station: PickupStationType;
  isMarked: boolean;
  isSelected: boolean;
  pickupStationRefs: object;
  index: number;
  isDelivery?: boolean;
  currentPosition?: Position;
  onClick: (id: string) => void;
  onMapLinkClick: (id: string) => void;
  onSelect?: (station: PickupStationType) => void;
};

export const PickupStation = ({
  station,
  isMarked,
  isSelected,
  pickupStationRefs,
  index,
  currentPosition,
  onClick,
  onSelect,
  onMapLinkClick,
}: Props) => {
  const { isDesktop, isMobile } = useMediaQueries();

  useEffect(() => {
    if (isMarked) {
      const { current } = pickupStationRefs[index];
      current.parentNode.scrollTop = current.offsetTop - current.parentNode.offsetTop - 16;
    }
  }, [isMarked]);

  useEffect(() => {
    if (isSelected && !isDesktop) {
      handleClick();
    }
  }, [isSelected, isDesktop]);

  const handleClick = () => {
    onClick(station.id);
  };

  const handleMapLinkClick = () => {
    isDesktop ? handleClick() : onMapLinkClick(station.id);
  };

  const handleSelect = () => {
    typeof onSelect === 'function' ? onSelect(station) : undefined;
  };

  return (
    <Box
      display="grid"
      gridGap={['na', 'na', 'm']}
      gridTemplateColumns={['1fr', '1fr', '1fr']}
      gridAutoRows="min-content"
      mx="s"
      my={['s', 's', 'm']}
      onClick={handleClick}
      id={`pickup-station-${index}-box`}
    >
      <StationInfo {...{ index, station, currentPosition }} />

      <Box alignSelf="left" display="flex" alignItems="left" flexDirection="column">
        <Box width={['100%', '100%', 'min-content']} my={['s', 'na', 'na']}>
          <Button
            id={`pickup-station-${index}-button`}
            aria-label="Bouton pour sélectionner une station de livraison"
            data-testid="pickup-station-button"
            type="button"
            preset={isSelected ? 'secondary' : 'subtle'}
            onClick={handleSelect}
          >
            {isSelected ? SELECTED_PICKUP_STATION : SELECT_PICKUP_STATION}
          </Button>
        </Box>
        {isMobile && (
          <Box mb="s" alignSelf="center">
            <button
              className="anchor-animated"
              aria-label="Bouton pour voir la station sur la carte"
              data-testid={`pickup-station-${index}-btn`}
              id={`pickup-station-${index}-btn`}
              type="button"
              onClick={handleMapLinkClick}
              css={css`
                font-size: 1.2rem;
              `}
            >
              {SEE_ON_THE_MAP}
            </button>
          </Box>
        )}
      </Box>
    </Box>
  );
};
